// ================================================
// SOUL FOOD BRIDGE VIEW PANEL
// Feature 8 - Side-by-side Company vs Soul Food intelligence
// ================================================
import React, { useState } from 'react';
import { 
  GitMerge, TrendingUp, TrendingDown, Users, Target,
  Calendar, AlertTriangle, CheckCircle2, ArrowRight,
  Building2, Sparkles, Zap
} from 'lucide-react';
import { CHIEF_OF_STAFF_COLORS, UNIQUE_IP_COLORS } from '../focus';
import { organizationMetrics, syncItems, formatDate } from '../../data/chiefOfStaffData';

const SoulFoodBridgePanel = () => {
  const [selectedAction, setSelectedAction] = useState(null);

  const companyData = organizationMetrics.company;
  const soulFoodData = organizationMetrics.soulFood;

  // Health dimensions for comparison
  const healthDimensions = [
    { 
      name: 'Execution Rate', 
      company: companyData.executionRate, 
      soulfood: soulFoodData.executionRate,
      max: 100,
      unit: '%'
    },
    { 
      name: 'On-Time Delivery', 
      company: companyData.onTimeDelivery, 
      soulfood: soulFoodData.onTimeDelivery,
      max: 100,
      unit: '%'
    },
    { 
      name: 'Velocity', 
      company: companyData.velocityThisWeek, 
      soulfood: soulFoodData.velocityThisWeek,
      max: 200,
      unit: ''
    },
    { 
      name: 'Decisions/Week', 
      company: companyData.decisionsThisWeek, 
      soulfood: soulFoodData.decisionsThisWeek,
      max: 25,
      unit: ''
    },
    { 
      name: 'Critical Items', 
      company: companyData.criticalItems, 
      soulfood: soulFoodData.criticalItems,
      max: 10,
      unit: '',
      inverse: true // Lower is better
    }
  ];

  const bridgeActions = [
    { id: 'meeting', label: 'Schedule Joint Leadership Meeting', icon: Calendar },
    { id: 'report', label: 'Create Cross-Entity Report', icon: Target },
    { id: 'align', label: 'Align Soul Food Timeline with Company Calendar', icon: GitMerge },
    { id: 'escalate', label: 'Escalate Soul Food Item to Executive', icon: AlertTriangle }
  ];

  const handleBridgeAction = (action) => {
    setSelectedAction(action.id);
    setTimeout(() => {
      alert(`Action: ${action.label}\n\nThis would initiate the cross-entity coordination workflow.`);
      setSelectedAction(null);
    }, 300);
  };

  const MetricCard = ({ label, value, trend, sublabel, color }) => (
    <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#0D1117' }}>
      <p className="text-lg font-bold flex items-center justify-center gap-1" style={{ color }}>
        {value}
        {trend === 'up' && <TrendingUp className="w-3 h-3 text-green-500" />}
        {trend === 'down' && <TrendingDown className="w-3 h-3 text-red-500" />}
      </p>
      <p className="text-xs text-gray-400">{label}</p>
      {sublabel && <p className="text-[10px] text-gray-500">{sublabel}</p>}
    </div>
  );

  return (
    <div className="p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <GitMerge className="w-5 h-5" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
        <div>
          <h3 className="text-sm font-bold text-white">Cross-Entity Intelligence</h3>
          <p className="text-xs text-gray-400">Company & Soul Food unified view</p>
        </div>
      </div>

      {/* Side-by-Side Columns */}
      <div className="grid grid-cols-2 gap-4">
        {/* Company Column */}
        <div 
          className="rounded-xl overflow-hidden"
          style={{ border: '1px solid #1E3A5F' }}
        >
          <div 
            className="px-4 py-3 flex items-center gap-2"
            style={{ backgroundColor: '#1E3A5F' }}
          >
            <Building2 className="w-4 h-4 text-white" />
            <span className="text-sm font-bold text-white">Company</span>
          </div>
          <div className="p-4 space-y-3" style={{ backgroundColor: '#161B22' }}>
            <MetricCard 
              label="Execution" 
              value={`${companyData.executionRate}%`}
              trend={companyData.velocityTrend}
              color="#3B82F6"
            />
            <MetricCard 
              label="Active Projects" 
              value={companyData.activeProjects}
              color="white"
            />
            <MetricCard 
              label="Team at Capacity" 
              value={companyData.teamMembersAtCapacity}
              color={companyData.teamMembersAtCapacity > 5 ? '#F59E0B' : 'white'}
            />
            <MetricCard 
              label="On-Time Rate" 
              value={`${companyData.onTimeDelivery}%`}
              color="#10B981"
            />
            <MetricCard 
              label="Critical Items" 
              value={companyData.criticalItems}
              color={companyData.criticalItems > 3 ? '#EF4444' : '#10B981'}
            />
            <div 
              className="p-3 rounded-lg"
              style={{ backgroundColor: '#0D1117', border: '1px solid #1E3A5F30' }}
            >
              <p className="text-xs text-gray-500 mb-1">Top Priority</p>
              <p className="text-sm text-white font-medium">{companyData.topPriority}</p>
            </div>
          </div>
        </div>

        {/* Soul Food Column */}
        <div 
          className="rounded-xl overflow-hidden"
          style={{ border: `1px solid ${UNIQUE_IP_COLORS.primary}` }}
        >
          <div 
            className="px-4 py-3 flex items-center gap-2"
            style={{ backgroundColor: UNIQUE_IP_COLORS.primary }}
          >
            <Sparkles className="w-4 h-4 text-white" />
            <span className="text-sm font-bold text-white">Soul Food</span>
          </div>
          <div className="p-4 space-y-3" style={{ backgroundColor: '#161B22' }}>
            <MetricCard 
              label="Execution" 
              value={`${soulFoodData.executionRate}%`}
              trend={soulFoodData.velocityTrend}
              color={UNIQUE_IP_COLORS.primary}
            />
            <MetricCard 
              label="Active Initiatives" 
              value={soulFoodData.activeProjects}
              color="white"
            />
            <MetricCard 
              label="Brand Health" 
              value={`${soulFoodData.brandHealth}%`}
              color={UNIQUE_IP_COLORS.primary}
            />
            <MetricCard 
              label="On-Time Rate" 
              value={`${soulFoodData.onTimeDelivery}%`}
              color="#10B981"
            />
            <MetricCard 
              label="Critical Items" 
              value={soulFoodData.criticalItems}
              color={soulFoodData.criticalItems > 0 ? '#F59E0B' : '#10B981'}
            />
            <div 
              className="p-3 rounded-lg"
              style={{ backgroundColor: '#0D1117', border: `1px solid ${UNIQUE_IP_COLORS.primary}30` }}
            >
              <p className="text-xs text-gray-500 mb-1">Top Priority</p>
              <p className="text-sm text-white font-medium">{soulFoodData.topPriority}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Health Comparison */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
      >
        <h4 className="text-xs font-bold text-gray-500 uppercase mb-4 flex items-center gap-2">
          <Zap className="w-3 h-3" />
          Health Comparison
        </h4>
        
        <div className="space-y-3">
          {healthDimensions.map((dim) => {
            const companyPercent = (dim.company / dim.max) * 100;
            const soulFoodPercent = (dim.soulfood / dim.max) * 100;
            
            return (
              <div key={dim.name} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">{dim.name}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-blue-400">{dim.company}{dim.unit}</span>
                    <span className="text-gray-600">vs</span>
                    <span style={{ color: UNIQUE_IP_COLORS.primary }}>{dim.soulfood}{dim.unit}</span>
                  </div>
                </div>
                <div className="flex gap-1 h-2">
                  <div 
                    className="h-full rounded-l"
                    style={{ 
                      width: `${companyPercent}%`,
                      backgroundColor: '#3B82F6'
                    }}
                  />
                  <div 
                    className="h-full rounded-r"
                    style={{ 
                      width: `${soulFoodPercent}%`,
                      backgroundColor: UNIQUE_IP_COLORS.primary
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sync Items */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
      >
        <h4 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
          <GitMerge className="w-3 h-3" />
          Cross-Entity Items
        </h4>
        
        <div className="space-y-2">
          {syncItems.map((item) => (
            <div 
              key={item.id}
              className="flex items-center gap-3 p-3 rounded-lg"
              style={{ backgroundColor: '#0D1117' }}
            >
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <ArrowRight className="w-3 h-3 text-gray-600" />
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: UNIQUE_IP_COLORS.primary }} />
              </div>
              <div className="flex-1">
                <p className="text-sm text-white">{item.title}</p>
                <p className="text-xs text-gray-500 capitalize">{item.type}</p>
              </div>
              {item.date && (
                <span className="text-xs text-gray-400">
                  {formatDate(item.date).split(',')[0]}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bridge Actions */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
      >
        <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">
          Bridge Actions
        </h4>
        
        <div className="grid grid-cols-2 gap-2">
          {bridgeActions.map((action) => {
            const Icon = action.icon;
            const isSelected = selectedAction === action.id;
            
            return (
              <button
                key={action.id}
                onClick={() => handleBridgeAction(action)}
                className="flex items-center gap-2 p-3 rounded-lg text-left transition-all"
                style={{ 
                  backgroundColor: isSelected ? CHIEF_OF_STAFF_COLORS.primary : '#0D1117',
                  border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}30`
                }}
              >
                <Icon 
                  className="w-4 h-4 flex-shrink-0" 
                  style={{ color: isSelected ? 'white' : CHIEF_OF_STAFF_COLORS.primary }}
                />
                <span 
                  className="text-xs font-medium"
                  style={{ color: isSelected ? 'white' : '#9CA3AF' }}
                >
                  {action.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SoulFoodBridgePanel;
